import { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Environment, OrthographicCamera, useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { FLOATING_CARD_GALLERY_PAIRS } from "@/data/floatingCardGalleryPool";
import { CANVAS_DPR } from "@/lib/perf";
import "./FloatingCardsGallery.css";

const GLB_URL = `${process.env.PUBLIC_URL || ""}/models/floating-cards.glb?v=2`;

const DRAG_ROT_Y = 0.01;
const DRAG_ROT_X = 0.006;
const ENTRY_POP_Z = -4.5;
const CAMERA_FIT_PADDING = 1.55;
const CARD_DISPLAY_SCALE = 0.78;
const CYCLE_INTERVAL_MS = 1600;
const SWAP_DURATION_S = 0.45;

const preparedScenes = new WeakSet();
const sceneCameraStore = new WeakMap();
/** @type {WeakMap<object, { leftMesh: THREE.Mesh, rightMesh: THREE.Mesh }>} */
const cardMeshRegistry = new WeakMap();
const textureCache = new Map();
const textureLoader = new THREE.TextureLoader();

function easeOutCubic(t) {
  const c = Math.min(Math.max(t, 0), 1);
  return 1 - (1 - c) ** 3;
}

function loadCardTexture(src) {
  if (textureCache.has(src)) return Promise.resolve(textureCache.get(src));
  return new Promise((resolve, reject) => {
    textureLoader.load(
      src,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        textureCache.set(src, tex);
        resolve(tex);
      },
      undefined,
      reject,
    );
  });
}

function cloneMaterialForCard(sourceMat) {
  if (!sourceMat) {
    return new THREE.MeshStandardMaterial({
      color: 0xffffff,
      metalness: 0.2,
      roughness: 0.2,
      transparent: true,
    });
  }
  const mat = sourceMat.clone();
  mat.color.setHex(0xffffff);
  mat.transparent = true;
  mat.opacity = 1;
  mat.depthWrite = true;
  return mat;
}

function prepareCardRootMaterials(cardRoot) {
  if (!cardRoot) return;
  const targets = cardRoot.isMesh ? [cardRoot] : [];
  cardRoot.traverse((obj) => {
    if (obj.isMesh) targets.push(obj);
  });
  targets.forEach((mesh) => {
    if (!mesh.material) return;
    if (Array.isArray(mesh.material)) {
      mesh.material = mesh.material.map((mat) => cloneMaterialForCard(mat));
      return;
    }
    mesh.material = cloneMaterialForCard(mesh.material);
  });
}

function applyCardTexture(cardRoot, texture) {
  if (!cardRoot || !texture) return;

  const targets = cardRoot.isMesh ? [cardRoot] : [];
  cardRoot.traverse((obj) => {
    if (obj.isMesh) targets.push(obj);
  });

  targets.forEach((mesh) => {
    const tex = texture.clone();
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;

    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((mat) => {
      if (!mat) return;
      mat.map = tex;
      mat.color.setHex(0xffffff);
      if (mat.emissive) mat.emissive.setHex(0x000000);
      mat.transparent = true;
      mat.opacity = 1;
      mat.needsUpdate = true;
    });
  });
}

function resolveCardMeshes(scene) {
  /** @type {THREE.Mesh[]} */
  const meshes = [];

  scene.traverse((obj) => {
    if (obj.isMesh) meshes.push(obj);
  });

  if (meshes.length >= 2) {
    scene.updateMatrixWorld(true);
    const posA = new THREE.Vector3();
    const posB = new THREE.Vector3();
    meshes.sort((a, b) => {
      a.getWorldPosition(posA);
      b.getWorldPosition(posB);
      return posA.x - posB.x;
    });
    return { leftMesh: meshes[0], rightMesh: meshes[meshes.length - 1] };
  }

  return {
    leftMesh: /** @type {THREE.Object3D | null} */ (scene.getObjectByName("Rectangle")),
    rightMesh: /** @type {THREE.Object3D | null} */ (scene.getObjectByName("Rectangle 2")),
  };
}

function meshUnderCard(hitObject, cardRoot) {
  let node = hitObject;
  while (node) {
    if (node === cardRoot) return true;
    node = node.parent;
  }
  return false;
}

function captureEmbeddedCamera(scene) {
  const glbCam =
    scene.getObjectByProperty("type", "OrthographicCamera") ||
    scene.getObjectByProperty("type", "PerspectiveCamera");
  if (!glbCam) return;

  scene.updateMatrixWorld(true);
  glbCam.updateMatrixWorld(true);

  sceneCameraStore.set(scene, {
    quaternion: new THREE.Quaternion().setFromRotationMatrix(glbCam.matrixWorld),
    near: glbCam.near,
    far: glbCam.far,
  });
}

function prepareFloatingCardsScene(scene) {
  if (preparedScenes.has(scene)) {
    return cardMeshRegistry.get(scene) ?? null;
  }
  captureEmbeddedCamera(scene);
  preparedScenes.add(scene);

  const remove = [];
  scene.traverse((obj) => {
    if (obj.isLight || obj.isCamera) remove.push(obj);
    if (!obj.isMesh || !obj.material) return;
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    mats.forEach((mat) => {
      if (!mat) return;
      if ("envMapIntensity" in mat) mat.envMapIntensity = 1.1;
      mat.needsUpdate = true;
    });
  });
  remove.forEach((obj) => obj.parent?.remove(obj));

  const cards = resolveCardMeshes(scene);
  if (cards.leftMesh) prepareCardRootMaterials(cards.leftMesh);
  if (cards.rightMesh) prepareCardRootMaterials(cards.rightMesh);
  cardMeshRegistry.set(scene, cards);
  return cards;
}

async function applyPairTextures(cards, pair) {
  if (!cards || !pair) return;

  const [leftTex, rightTex] = await Promise.all([
    loadCardTexture(pair.left.src),
    loadCardTexture(pair.right.src),
  ]);

  if (cards.leftMesh) applyCardTexture(cards.leftMesh, leftTex);
  if (cards.rightMesh) applyCardTexture(cards.rightMesh, rightTex);
}

function preloadPairTextures(pair) {
  if (!pair) return;
  loadCardTexture(pair.left.src).catch(() => {});
  loadCardTexture(pair.right.src).catch(() => {});
}

function fitOrthoCameraToScene(root, camera, viewport, sceneKey, padding = 1.1) {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  if (box.isEmpty()) return;

  const center = box.getCenter(new THREE.Vector3());
  const boxSize = box.getSize(new THREE.Vector3());
  const stored = sceneKey ? sceneCameraStore.get(sceneKey) : null;

  if (stored?.quaternion) {
    camera.quaternion.copy(stored.quaternion);
    const viewBack = new THREE.Vector3(0, 0, 1).applyQuaternion(stored.quaternion);
    camera.position.copy(center).addScaledVector(viewBack, Math.max(boxSize.length() * 1.35, 6));
  } else {
    camera.position.set(center.x, center.y, center.z + Math.max(boxSize.z, 1) * 2.4);
    camera.lookAt(center);
  }

  camera.near = 0.1;
  camera.far = Math.max(boxSize.length() * 12, 2000);

  const inv = camera.quaternion.clone().invert();
  const corners = [
    new THREE.Vector3(box.min.x, box.min.y, box.min.z),
    new THREE.Vector3(box.min.x, box.min.y, box.max.z),
    new THREE.Vector3(box.min.x, box.max.y, box.min.z),
    new THREE.Vector3(box.min.x, box.max.y, box.max.z),
    new THREE.Vector3(box.max.x, box.min.y, box.min.z),
    new THREE.Vector3(box.max.x, box.min.y, box.max.z),
    new THREE.Vector3(box.max.x, box.max.y, box.min.z),
    new THREE.Vector3(box.max.x, box.max.y, box.max.z),
  ];

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  corners.forEach((corner) => {
    const local = corner.clone().sub(center).applyQuaternion(inv);
    minX = Math.min(minX, local.x);
    maxX = Math.max(maxX, local.x);
    minY = Math.min(minY, local.y);
    maxY = Math.max(maxY, local.y);
  });

  let halfW = Math.max((maxX - minX) * 0.5, 0.5) * padding;
  let halfH = Math.max((maxY - minY) * 0.5, 0.5) * padding;
  const aspect = viewport.width / Math.max(viewport.height, 1);
  if (halfW / halfH > aspect) halfH = halfW / aspect;
  else halfW = halfH * aspect;

  camera.left = -halfW;
  camera.right = halfW;
  camera.top = halfH;
  camera.bottom = -halfH;
  camera.zoom = 1;
  camera.updateProjectionMatrix();
}

function FloatingCardsLighting() {
  return (
    <>
      <ambientLight intensity={0.65} />
      <hemisphereLight args={["#f0f0ff", "#1a1a24", 0.5]} />
      <directionalLight position={[6, 8, 4]} intensity={0.9} />
      <directionalLight position={[-5, 3, -4]} intensity={0.4} />
      <Environment preset="city" />
    </>
  );
}

function FloatingCardsModel({ entryProgressRef, pairIndex, pairs, onPairApplied }) {
  const pivotRef = useRef(null);
  const flyRef = useRef(null);
  const spinRef = useRef(null);
  const offsetRef = useRef(null);
  const dragRotRef = useRef({ x: 0, y: 0 });
  const draggingRef = useRef(false);
  const lastClient = useRef({ x: 0, y: 0 });
  const meshPathRef = useRef({ left: "", right: "" });
  const cardsRef = useRef(null);
  const swapTRef = useRef(1);
  const swappingRef = useRef(false);
  const pendingPairRef = useRef(null);
  const appliedPairIndexRef = useRef(-1);
  const cameraFitTickRef = useRef(0);
  const { scene: sourceScene } = useGLTF(GLB_URL);
  const scene = useMemo(() => sourceScene.clone(true), [sourceScene]);
  const { camera, size, gl } = useThree();
  const navigate = useNavigate();

  const goTo = useCallback((path) => navigate(path), [navigate]);

  const syncMeshPaths = useCallback((pair) => {
    if (!pair) return;
    meshPathRef.current = {
      left: pair.left.path,
      right: pair.right.path,
    };
  }, []);

  const commitPair = useCallback(
    async (index) => {
      const pair = pairs[index];
      const cards = cardsRef.current;
      if (!pair || !cards) return;
      await applyPairTextures(cards, pair);
      syncMeshPaths(pair);
      appliedPairIndexRef.current = index;
      onPairApplied?.(index);
      const next = pairs[(index + 1) % pairs.length];
      preloadPairTextures(next);
    },
    [onPairApplied, pairs, syncMeshPaths],
  );

  useLayoutEffect(() => {
    cardsRef.current = prepareFloatingCardsScene(scene);
    scene.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(scene);
    const center = box.getCenter(new THREE.Vector3());
    if (pivotRef.current) pivotRef.current.position.copy(center);
    if (offsetRef.current) offsetRef.current.position.copy(center).multiplyScalar(-1);
  }, [scene]);

  useLayoutEffect(() => {
    if (pivotRef.current) fitOrthoCameraToScene(pivotRef.current, camera, size, scene, CAMERA_FIT_PADDING);
  }, [camera, scene, size]);

  useEffect(() => {
    if (!pairs.length || appliedPairIndexRef.current >= 0) return;
    commitPair(0);
  }, [commitPair, pairs]);

  useEffect(() => {
    if (!pairs.length || pairIndex === appliedPairIndexRef.current) return;
    if (appliedPairIndexRef.current < 0) return;

    pendingPairRef.current = pairIndex;
    swappingRef.current = true;
    swapTRef.current = 0;
  }, [pairIndex, pairs.length]);

  useLayoutEffect(() => {
    const el = gl.domElement;
    const opts = { passive: false };

    const onPointerDown = (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      draggingRef.current = true;
      lastClient.current = { x: e.clientX, y: e.clientY };
      el.setPointerCapture(e.pointerId);
      el.style.cursor = "grabbing";
    };

    const endDrag = (e) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      el.style.cursor = "grab";
    };

    const onPointerMove = (e) => {
      if (!draggingRef.current) return;
      e.preventDefault();
      const dx = e.clientX - lastClient.current.x;
      const dy = e.clientY - lastClient.current.y;
      lastClient.current = { x: e.clientX, y: e.clientY };
      dragRotRef.current.y += dx * DRAG_ROT_Y;
      dragRotRef.current.x = THREE.MathUtils.clamp(dragRotRef.current.x + dy * DRAG_ROT_X, -0.5, 0.5);
    };

    const onClick = (e) => {
      if (draggingRef.current || swappingRef.current) return;
      const rect = el.getBoundingClientRect();
      const pointer = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      );
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(pointer, camera);
      const cards = cardsRef.current;
      const targets = [cards?.leftMesh, cards?.rightMesh].filter(Boolean);
      const hit = raycaster.intersectObjects(targets, true)[0];
      if (!hit || !cards) return;

      if (cards.leftMesh && meshUnderCard(hit.object, cards.leftMesh)) {
        goTo(meshPathRef.current.left);
        return;
      }
      if (cards.rightMesh && meshUnderCard(hit.object, cards.rightMesh)) {
        goTo(meshPathRef.current.right);
      }
    };

    el.style.cursor = "grab";
    el.style.touchAction = "none";
    el.addEventListener("pointerdown", onPointerDown, opts);
    el.addEventListener("pointerup", endDrag, opts);
    el.addEventListener("pointercancel", endDrag, opts);
    el.addEventListener("pointermove", onPointerMove, opts);
    el.addEventListener("click", onClick);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown, opts);
      el.removeEventListener("pointerup", endDrag, opts);
      el.removeEventListener("pointercancel", endDrag, opts);
      el.removeEventListener("pointermove", onPointerMove, opts);
      el.removeEventListener("click", onClick);
      el.style.cursor = "";
      el.style.touchAction = "";
    };
  }, [camera, gl, goTo, scene]);

  useFrame((_, delta) => {
    const entryT = easeOutCubic(entryProgressRef.current);
    cameraFitTickRef.current += 1;
    if (pivotRef.current && entryT < 0.96 && cameraFitTickRef.current % 5 === 0) {
      fitOrthoCameraToScene(pivotRef.current, camera, size, scene, CAMERA_FIT_PADDING);
    }
    if (flyRef.current && !swappingRef.current) {
      flyRef.current.position.z = THREE.MathUtils.lerp(ENTRY_POP_Z, 0, entryT);
      flyRef.current.position.y = THREE.MathUtils.lerp(-0.6, 0, entryT);
      const s = THREE.MathUtils.lerp(0.1 * CARD_DISPLAY_SCALE, CARD_DISPLAY_SCALE, entryT);
      flyRef.current.scale.set(s, s, s);
      flyRef.current.rotation.x = THREE.MathUtils.lerp(0.22, 0, entryT);
    }

    if (swappingRef.current && flyRef.current && appliedPairIndexRef.current >= 0) {
      swapTRef.current = Math.min(swapTRef.current + delta / SWAP_DURATION_S, 1);
      const wave = Math.sin(swapTRef.current * Math.PI);
      flyRef.current.position.z = -wave * 2.2;
      flyRef.current.rotation.y = wave * 0.12;

      if (swapTRef.current >= 0.48 && pendingPairRef.current !== null) {
        const idx = pendingPairRef.current;
        pendingPairRef.current = null;
        commitPair(idx);
      }

      if (swapTRef.current >= 1) {
        swappingRef.current = false;
        swapTRef.current = 1;
        if (flyRef.current) {
          flyRef.current.position.z = 0;
          flyRef.current.rotation.y = 0;
        }
      }
    }

    if (!spinRef.current) return;
    spinRef.current.rotation.order = "YXZ";
    spinRef.current.rotation.y = dragRotRef.current.y;
    spinRef.current.rotation.x = dragRotRef.current.x;
  });

  return (
    <group ref={pivotRef}>
      <group ref={flyRef}>
        <group ref={spinRef}>
          <group ref={offsetRef}>
            <primitive object={scene} />
          </group>
        </group>
      </group>
    </group>
  );
}

useGLTF.preload(GLB_URL);

/** Scroll-driven entry progress for portfolio sticky section. */
export function measureFloatingCardsEntryProgress(el) {
  const vh = window.innerHeight;
  const rect = el.getBoundingClientRect();

  if (rect.top > 0) {
    const approach = 1 - Math.min(rect.top / vh, 1);
    return approach * 0.45;
  }

  const scrollY = -rect.top;
  const span = Math.max(el.offsetHeight - vh, 1);
  return Math.min(0.45 + (scrollY / span) * 0.55, 1);
}

/**
 * Interactive 3D portfolio card pairs — drag, click-to-navigate, auto cycle.
 */
export default function FloatingCardsGallery({
  className = "",
  variant = "portfolio",
  entryProgress = 1,
  inView = true,
  autoCycle = true,
  autoCycleRequiresFullEntry = false,
  showHints = true,
  hintOpacity,
  testId = "floating-cards-gallery",
}) {
  const entryProgressRef = useRef(entryProgress);
  const [pairIndex, setPairIndex] = useState(0);
  const [displayPairIndex, setDisplayPairIndex] = useState(0);
  const pairs = useMemo(() => FLOATING_CARD_GALLERY_PAIRS, []);

  entryProgressRef.current = entryProgress;

  useEffect(() => {
    if (!autoCycle || !inView || pairs.length <= 1) return undefined;

    const timer = setInterval(() => {
      if (autoCycleRequiresFullEntry && entryProgressRef.current < 0.95) return;
      setPairIndex((i) => (i + 1) % pairs.length);
    }, CYCLE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [autoCycle, autoCycleRequiresFullEntry, inView, pairs.length]);

  const resolvedHintOpacity =
    hintOpacity ?? Math.min(Math.max((entryProgress - 0.7) / 0.25, 0), 1);

  const currentPair = pairs[displayPairIndex];
  const pairLabel =
    pairs.length > 0 && currentPair
      ? `${displayPairIndex + 1} / ${pairs.length} · ${currentPair.left.title} · ${currentPair.right.title}`
      : "";

  const variantClass =
    variant === "hero" ? "ds-floating-cards-gallery--hero" : "ds-floating-cards-gallery--portfolio";

  return (
    <div
      className={`ds-floating-cards-gallery ${variantClass}${className ? ` ${className}` : ""}`}
      data-testid={testId}
    >
      {pairs.length > 0 && inView ? (
        <Canvas
          dpr={CANVAS_DPR}
          gl={{ antialias: true, alpha: true }}
          onCreated={({ gl: renderer }) => {
            renderer.setClearColor(0x000000, 0);
            renderer.toneMapping = THREE.ACESFilmicToneMapping;
            renderer.toneMappingExposure = 1.05;
          }}
        >
          <OrthographicCamera makeDefault manual near={0.1} far={5000} />
          <Suspense fallback={null}>
            <FloatingCardsLighting />
            <FloatingCardsModel
              entryProgressRef={entryProgressRef}
              pairIndex={pairIndex}
              pairs={pairs}
              onPairApplied={setDisplayPairIndex}
            />
          </Suspense>
        </Canvas>
      ) : null}
      {showHints ? (
        <>
          <p className="ds-floating-cards-gallery__hint" style={{ opacity: resolvedHintOpacity }}>
            카드 클릭 · 드래그로 돌려보기 · {pairs.length}쌍 자동 순환
          </p>
          {pairLabel ? (
            <p className="ds-floating-cards-gallery__pair-label" style={{ opacity: resolvedHintOpacity }}>
              {pairLabel}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
